import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Eye, Mail, RefreshCw, Search, Send, X } from "lucide-react";
import SortableTh from "@/components/SortableTh";
import { toast } from "sonner";

const STATUS_LABELS = {
    sent: "Enviado",
    delivered: "Entregado",
    bounced: "Rebotado",
    complained: "Spam",
    failed: "Fallido",
    skipped: "Omitido",
    queued: "En cola",
    opened: "Abierto",
    clicked: "Click",
    delayed: "Demorado",
};

const KNOWN_CHANNELS = [
    "welcome",
    "password_reset",
    "email_ticket_created",
    "email_ticket_updated",
    "email_weekly_report",
    "email_weekly_my_scope",
    "email_view_alerts",
    "saved_report",
    "custom_notice",
    "resend_manual",
    "webhook",
];

function StatusPill({ status }) {
    return (
        <span
            data-testid={`emaillog-status-${status}`}
            className={`email-status-pill email-status-${status}`}
        >
            {STATUS_LABELS[status] || status}
        </span>
    );
}

function PreviewModal({ log, onClose }) {
    const [html, setHtml] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const res = await api.get(`/email-logs/${log.log_id}/preview`, { responseType: "text", transformResponse: [(d) => d] });
                if (!cancelled) setHtml(typeof res.data === "string" ? res.data : "");
            } catch (e) {
                if (!cancelled) setError(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [log.log_id]);

    return (
        <div className="modal-backdrop" data-testid="emaillogs-preview-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
            <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 10, width: "min(900px, 95vw)", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14, borderBottom: "1px solid var(--border)" }}>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ color: "var(--text)", fontWeight: 600, fontSize: 14 }}>Vista previa del correo</div>
                        <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <b>Para:</b> {log.to} &nbsp;·&nbsp; <b>Asunto:</b> {log.subject}
                        </div>
                    </div>
                    <button className="btn" onClick={onClose} data-testid="emaillogs-preview-close" style={{ flexShrink: 0 }}><X size={14} /></button>
                </div>
                <div style={{ flex: 1, overflow: "hidden", background: "#fff" }}>
                    {loading && <div style={{ padding: 24, color: "var(--text-muted)", fontFamily: "sans-serif" }}>Cargando…</div>}
                    {error && <div style={{ padding: 24, color: "#DC2626", fontFamily: "sans-serif" }}>{error}</div>}
                    {!loading && !error && (
                        <iframe
                            title="email-preview"
                            data-testid="emaillogs-preview-iframe"
                            srcDoc={html}
                            sandbox=""
                            style={{ width: "100%", height: "70vh", border: 0, background: "#fff" }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}


function ResendModal({ log, onClose, onSent }) {
    const [to, setTo] = useState(log?.to || "");
    const [cc, setCc] = useState((log?.cc || []).join(", "));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true); setError("");
        try {
            const cc_list = cc.split(/[,\s]+/).map((x) => x.trim()).filter((x) => x.includes("@"));
            const { data } = await api.post(`/email-logs/${log.log_id}/resend`, { to: to.trim(), cc: cc_list });
            if (data.ok) toast.success(`Correo reenviado a ${data.to}`);
            else toast.warning(`Reenvío registrado como fallido. Revisa el log.`);
            onSent();
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    return (
        <div className="modal-backdrop" data-testid="emaillogs-resend-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
            <div className="modal card" style={{ width: "min(480px, 92vw)", padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <h3 style={{ margin: 0, color: "var(--text)" }}>Reenviar correo</h3>
                    <button className="btn" onClick={onClose} data-testid="emaillogs-resend-close"><X size={14} /></button>
                </div>
                <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "0 0 14px" }}>
                    Reenvía este correo corrigiendo el destinatario o Cc. Se generará un nuevo registro en la bandeja.
                </p>
                <form onSubmit={submit}>
                    <div className="field">
                        <label>Asunto</label>
                        <input className="input" value={log?.subject || ""} disabled />
                    </div>
                    <div className="field">
                        <label>Destinatario</label>
                        <input className="input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="emaillogs-resend-to" autoFocus required />
                    </div>
                    <div className="field">
                        <label>Cc (separado por comas)</label>
                        <input className="input" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="emaillogs-resend-cc" placeholder="cc1@x.com, cc2@y.com" />
                    </div>
                    {error && <div style={{ color: "#DC2626", marginTop: 8, fontSize: 13 }}>{error}</div>}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                        <button type="button" className="btn" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="btn-primary" disabled={loading} data-testid="emaillogs-resend-submit" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <Send size={14} /> {loading ? "Enviando…" : "Reenviar"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function EmailLogs() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [skip, setSkip] = useState(0);
    const limit = 50;

    const [filterStatus, setFilterStatus] = useState("");
    const [filterChannel, setFilterChannel] = useState("");
    const [filterTo, setFilterTo] = useState("");
    const [filterQ, setFilterQ] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const [sort, setSort] = useState({ field: "created_at", dir: "desc" });
    const [resendLog, setResendLog] = useState(null);
    const [previewLog, setPreviewLog] = useState(null);

    const load = async (newSkip = 0, overrides = null, sortOverride = null) => {
        setLoading(true); setError("");
        try {
            const f = overrides || { status: filterStatus, channel: filterChannel, to: filterTo, q: filterQ, date_from: dateFrom, date_to: dateTo };
            const srt = sortOverride || sort;
            const params = { limit, skip: newSkip, sort_by: srt.field, sort_dir: srt.dir };
            if (f.status) params.status = f.status;
            if (f.channel) params.channel = f.channel;
            if (f.to && f.to.trim()) params.to = f.to.trim();
            if (f.q && f.q.trim()) params.q = f.q.trim();
            if (f.date_from) params.date_from = f.date_from;
            if (f.date_to) params.date_to = f.date_to;
            const [logs, st] = await Promise.all([
                api.get("/email-logs", { params }),
                api.get("/email-logs/stats"),
            ]);
            setItems(logs.data.items || []);
            setTotal(logs.data.total || 0);
            setSkip(newSkip);
            setStats(st.data || {});
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(0); /* eslint-disable-next-line */ }, []);

    // Re-load when sort changes (server-side)
    const onSortChange = (s) => { setSort(s); load(skip, null, s); };

    const onSearch = (e) => { e.preventDefault(); load(0); };
    const reset = () => {
        setFilterStatus(""); setFilterChannel(""); setFilterTo(""); setFilterQ(""); setDateFrom(""); setDateTo("");
        load(0, { status: "", channel: "", to: "", q: "", date_from: "", date_to: "" });
    };

    const channels = useMemo(() => KNOWN_CHANNELS, []);

    const thMuted = { padding: "10px 12px", textAlign: "left", color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: "1px solid var(--border)" };

    return (
        <div data-testid="email-logs-page" className="space-y-6">
            <div className="page-head">
                <div>
                    <h1><Mail size={20} style={{ marginRight: 8, verticalAlign: "-3px" }} /> Bandeja de envíos</h1>
                    <div className="subtitle">Auditoría de correos transaccionales: enviados, rebotados, omitidos por preferencia o fallidos.</div>
                </div>
                <button
                    type="button"
                    className="btn"
                    data-testid="emaillogs-refresh"
                    onClick={() => load(skip)}
                    disabled={loading}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                    <RefreshCw size={14} className={loading ? "spin" : ""} /> Actualizar
                </button>
            </div>

            {/* KPI bar */}
            <div className="card" data-testid="emaillogs-stats">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px,1fr))", gap: 12, padding: 16 }}>
                    {["sent", "delivered", "bounced", "failed", "skipped", "complained", "opened", "clicked"].map((s) => (
                        <div key={s} data-testid={`emaillogs-stat-${s}`} style={{ padding: 12, background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 8, textAlign: "center" }}>
                            <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>{STATUS_LABELS[s]}</div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text)", marginTop: 4 }}>{stats[s] || 0}</div>
                        </div>
                    ))}
                    <div style={{ padding: 12, background: "var(--bg-1)", border: "1px solid var(--accent)", borderRadius: 8, textAlign: "center" }} data-testid="emaillogs-stat-total">
                        <div style={{ fontSize: 11, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".06em" }}>Total 30 días</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text)", marginTop: 4 }}>{stats.total || 0}</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <form className="card" onSubmit={onSearch} data-testid="emaillogs-filters" style={{ padding: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                    <div className="field">
                        <label>Status</label>
                        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="select" data-testid="emaillogs-filter-status">
                            <option value="">Todos</option>
                            {Object.keys(STATUS_LABELS).map((s) => (
                                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                            ))}
                        </select>
                    </div>
                    <div className="field">
                        <label>Canal / template</label>
                        <select value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)} className="select" data-testid="emaillogs-filter-channel">
                            <option value="">Todos</option>
                            {channels.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="field">
                        <label>Destinatario contiene</label>
                        <input className="input" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} placeholder="email@..." data-testid="emaillogs-filter-to" />
                    </div>
                    <div className="field">
                        <label>Asunto / Resend ID</label>
                        <input className="input" value={filterQ} onChange={(e) => setFilterQ(e.target.value)} placeholder="Texto libre" data-testid="emaillogs-filter-q" />
                    </div>
                    <div className="field">
                        <label>Desde</label>
                        <input type="date" className="input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="emaillogs-filter-from" />
                    </div>
                    <div className="field">
                        <label>Hasta</label>
                        <input type="date" className="input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="emaillogs-filter-to-date" />
                    </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button type="submit" className="btn-primary" data-testid="emaillogs-filter-apply" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <Search size={14} /> Filtrar
                    </button>
                    <button type="button" className="btn" onClick={reset} data-testid="emaillogs-filter-reset">Limpiar</button>
                </div>
            </form>

            {/* Errors */}
            {error && <div className="card" style={{ padding: 14, color: "#DC2626", borderColor: "var(--accent)" }}>{error}</div>}

            {/* Table */}
            <div className="card" data-testid="emaillogs-table-card">
                <div style={{ overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: "var(--bg-2)" }}>
                                <SortableTh field="created_at" sort={sort} onChange={onSortChange}>Fecha</SortableTh>
                                <SortableTh field="status" sort={sort} onChange={onSortChange}>Estado</SortableTh>
                                <SortableTh field="to" sort={sort} onChange={onSortChange}>Destinatario</SortableTh>
                                <th style={thMuted}>Cc</th>
                                <SortableTh field="subject" sort={sort} onChange={onSortChange}>Asunto</SortableTh>
                                <SortableTh field="channel" sort={sort} onChange={onSortChange}>Canal</SortableTh>
                                <th style={thMuted}>Resend ID</th>
                                <th style={thMuted}>Detalle</th>
                                <th style={{ ...thMuted, textAlign: "right" }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={9} style={{ padding: 24, textAlign: "center", color: "var(--text-muted)" }} data-testid="emaillogs-empty">Sin correos registrados con estos filtros.</td>
                                </tr>
                            )}
                            {items.map((it) => (
                                <tr key={it.log_id} data-testid={`emaillog-row-${it.log_id}`} style={{ borderBottom: "1px solid var(--border)" }}>
                                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "var(--text)", fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}>
                                        {it.created_at ? new Date(it.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}
                                    </td>
                                    <td style={{ padding: "10px 12px" }}><StatusPill status={it.status} /></td>
                                    <td style={{ padding: "10px 12px", color: "var(--text)" }}>{it.to}</td>
                                    <td style={{ padding: "10px 12px", color: "var(--text-muted)", fontFamily: "JetBrains Mono, monospace", fontSize: 12 }}>
                                        {(it.cc || []).length ? (it.cc || []).join(", ") : "—"}
                                    </td>
                                    <td style={{ padding: "10px 12px", color: "var(--text)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.subject}>{it.subject}</td>
                                    <td style={{ padding: "10px 12px", color: "var(--text-muted)", fontSize: 12 }}>{it.channel || it.template || "—"}</td>
                                    <td style={{ padding: "10px 12px", fontFamily: "JetBrains Mono, monospace", fontSize: 11, color: "var(--text-muted)" }} title={it.resend_id || ""}>
                                        {it.resend_id ? `${it.resend_id.slice(0, 8)}…` : "—"}
                                    </td>
                                    <td style={{ padding: "10px 12px", color: it.error ? "#DC2626" : "var(--text-muted)", fontSize: 12, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }} title={it.error || ""}>
                                        {it.error || (it.has_attachments ? "(con adjuntos)" : "—")}
                                    </td>
                                    <td style={{ padding: "10px 12px", textAlign: "right" }}>
                                        <div style={{ display: "inline-flex", gap: 6 }}>
                                            <button
                                                type="button"
                                                className="btn"
                                                onClick={() => setPreviewLog(it)}
                                                data-testid={`emaillogs-preview-btn-${it.log_id}`}
                                                title="Ver vista previa del correo HTML"
                                                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: "4px 10px" }}
                                            >
                                                <Eye size={12} /> Ver
                                            </button>
                                            <button
                                                type="button"
                                                className="btn"
                                                onClick={() => setResendLog(it)}
                                                data-testid={`emaillogs-resend-btn-${it.log_id}`}
                                                title="Reenviar corrigiendo destinatario o Cc"
                                                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: "4px 10px" }}
                                            >
                                                <Send size={12} /> Reenviar
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 12, borderTop: "1px solid var(--border)", color: "var(--text-muted)", fontSize: 12 }}>
                    <div data-testid="emaillogs-pagination-info">{total === 0 ? "Sin resultados" : `${skip + 1}-${Math.min(skip + limit, total)} de ${total}`}</div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" className="btn" disabled={skip === 0 || loading} onClick={() => load(Math.max(0, skip - limit))} data-testid="emaillogs-prev">Anterior</button>
                        <button type="button" className="btn" disabled={skip + limit >= total || loading} onClick={() => load(skip + limit)} data-testid="emaillogs-next">Siguiente</button>
                    </div>
                </div>
            </div>

            {resendLog && (
                <ResendModal
                    log={resendLog}
                    onClose={() => setResendLog(null)}
                    onSent={() => { setResendLog(null); load(skip); }}
                />
            )}
            {previewLog && (
                <PreviewModal
                    log={previewLog}
                    onClose={() => setPreviewLog(null)}
                />
            )}
        </div>
    );
}
